import i18n from 'i18next'

export type TranslationLoadResult = 'ready' | 'failed' | 'superseded'

const FALLBACK_LANGUAGE = 'en'

let currentRevision = 0
let pendingRequests: Promise<TranslationLoadResult> = Promise.resolve('ready')

const attemptLanguage = (locale: string): Promise<boolean> => new Promise((resolve) => {
    let loadFailed = false

    const handleFailedLoading = (failedLanguage: string): void => {
        if (failedLanguage === locale) {
            loadFailed = true
        }
    }

    i18n.on('failedLoading', handleFailedLoading)

    i18n.changeLanguage(locale, (error) => {
        i18n.off('failedLoading', handleFailedLoading)
        resolve(!error && !loadFailed)
    })
})

const runRequest = async (locale: string, revision: number): Promise<TranslationLoadResult> => {
    if (revision !== currentRevision) {
        return 'superseded'
    }

    const loaded = await attemptLanguage(locale)

    if (revision !== currentRevision) {
        return 'superseded'
    }

    if (loaded) {
        return 'ready'
    }

    if (locale === FALLBACK_LANGUAGE) {
        return 'failed'
    }

    const recovered = await attemptLanguage(FALLBACK_LANGUAGE)

    if (revision !== currentRevision) {
        return 'superseded'
    }

    return recovered ? 'ready' : 'failed'
}

export const loadTranslations = (locale: string): Promise<TranslationLoadResult> => {
    if (locale === '') {
        return Promise.resolve('superseded')
    }

    currentRevision += 1

    const revision = currentRevision
    const startRequest = (): Promise<TranslationLoadResult> => runRequest(locale, revision)

    pendingRequests = pendingRequests.then(startRequest, startRequest)

    return pendingRequests
}
